/**
 * DeskeyPaint — MS Paint / jspaint parity (hybrid Konva engine)
 * Tools: Select(rect+lasso), Pen, Brush(tips), Airbrush, Eraser, Eyedropper,
 *        Fill, Line, Curve(3-click), Arrow, DoubleArrow, Polygon, Shapes, Text, Magnifier
 * Clipboard: Cut/Copy/Paste via Ctrl+X/C/V
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Circle, Ellipse, Line, Arrow, Star, RegularPolygon, Text, Transformer, Group, Image as KImage, Path } from 'react-konva';
import useCanvasCollab from '../hooks/useCanvasCollab';
// import { newRoomId } from '../lib/collabRoom';  // Go Live hidden for now
import CollabBadge from './CollabBadge';
import { Scissors, Copy, ClipboardPaste } from 'lucide-react';
import {
  initialHistory, pushHistory, stepHistory, isVectorFillable, withVectorFill,
  isCenterAnchored, resizedBox, mapPoints, dedupePolygonPoints, floodFillImageData,
  ZOOMS, zoomStep, hasDrawing,
} from './paint/paintModel';
import { confirmDialog } from './ConfirmDialog';

const COLORS = ['#1e293b','#374151','#6b7280','#9ca3af','#ef4444','#f97316','#eab308','#22c55e','#34A853','#0d9488','#3b82f6','#6366f1','#8b5cf6','#ec4899','#ffffff'];
const FONTS = ['Arial','Helvetica','Georgia','Courier New','Verdana','Trebuchet MS','Impact','Comic Sans MS','Times New Roman','Palatino','Garamond','Bookman'];
const SIZES = [8,10,12,14,16,18,20,24,28,32,36,42,48,56,64,72,96];
const SHAPES = [
  {id:'rect',label:'Rectangle',cat:'basic'},{id:'roundRect',label:'Rounded Rect',cat:'basic'},
  {id:'ellipse',label:'Ellipse',cat:'basic'},{id:'circle',label:'Circle',cat:'basic'},
  {id:'triangle',label:'Triangle',cat:'basic'},{id:'diamond',label:'Diamond',cat:'basic'},
  {id:'star',label:'Star',cat:'basic'},{id:'hexagon',label:'Hexagon',cat:'flow'},
  {id:'pentagon',label:'Pentagon',cat:'flow'},{id:'octagon',label:'Octagon',cat:'flow'},
  {id:'trapezoid',label:'Trapezoid',cat:'flow'},{id:'cylinder',label:'Cylinder',cat:'flow'},
  {id:'cloud',label:'Cloud',cat:'flow'},
];
const LINE_WIDTHS = [1,2,3,4,5,8,12];
const BRUSH_TIPS = [
  { id:'circle', label:'●' },
  { id:'square', label:'■' },
  { id:'slash', label:'╱' },
];
// Editor chrome (text-box borders, placeholders, marquees, previews) carries
// this name so it is hidden whenever the stage is flattened or exported.
const OVERLAY = 'ui-overlay';
const MARCH_INTERVAL_MS = 120;
const AIRBRUSH_INTERVAL_MS = 40;
const FILL_MODES = [
  { id:'outline', label:'Outline', desc:'Stroke only' },
  { id:'both', label:'Both', desc:'Stroke + Fill' },
  { id:'fill', label:'Fill', desc:'Fill only' },
];

const hexToRgba = (hex) => {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16), 255];
};
const rgbToHex = (r,g,b) => '#' + [r,g,b].map(v => v.toString(16).padStart(2,'0')).join('');

export default function DeskeyPaint({ onSave, onClose, roomId = null }) {
  const stageRef = useRef(null);
  const trRef = useRef(null);
  const fileRef = useRef(null);
  const textRef = useRef(null);
  const rasterRef = useRef(null);
  const airbrushTimerRef = useRef(null);
  const [tool, setTool] = useState('select');
  const [objects, setObjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [strokeColor, setStrokeColor] = useState('#1e293b');
  const [fillColor, setFillColor] = useState('');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fillMode, setFillMode] = useState('outline'); // outline|both|fill
  const [brushTip, setBrushTip] = useState('circle');
  const [fontSize, setFontSize] = useState(20);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontBold, setFontBold] = useState(false);
  const [fontItalic, setFontItalic] = useState(false);
  const [fontUnderline, setFontUnderline] = useState(false);
  const [textAlign, setTextAlign] = useState('left');
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [shapeType, setShapeType] = useState('rect');
  const [showGrid, setShowGrid] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [images, setImages] = useState({});
  const [rasterImg, setRasterImg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  // ── Realtime collab — "Go Live" starts a room; the share link opens
  //    /live/<roomId>, which mounts this component with `roomId` set.
  //    Vector objects and the raster layer (fill, airbrush, image ops)
  //    are both shared; see useCanvasCollab for the sync rules.
  const [collabRoom, setCollabRoom] = useState(roomId);
  // Show a raster snapshot and keep the hidden raster canvas in step with it
  // (fill/selection/airbrush draw on that canvas, so it must never go stale).
  // The image is set synchronously so the shared `raster` field matches what
  // was applied; it is painted into the hidden canvas once decoded.
  const showRaster = (src) => {
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    if (!src) {
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, CW, CH);
      setRasterImg(null);
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      ctx.clearRect(0, 0, CW, CH);
      ctx.drawImage(img, 0, 0);
      stageRef.current?.batchDraw();
    };
    img.src = src;
    setRasterImg(img);
  };
  const { peerCount, status: collabStatus } = useCanvasCollab(collabRoom, {
    state: { objects, raster: rasterImg?.src || null },
    apply: (patch) => {
      if (Array.isArray(patch.objects)) setObjects(patch.objects);
      if ('raster' in patch) showRaster(patch.raster);
    },
  });
  // const startCollab = () => setCollabRoom(newRoomId('paint'));  // Go Live hidden for now

  // jspaint-style selection
  const [selection, setSelection] = useState(null); // {x,y,w,h, dashOffset}
  const [floatSel, setFloatSel] = useState(null);   // {x,y,w,h,image(HTMLImage), origX, origY}
  const clipboardRef = useRef(null); // {dataURL, w, h}
  // curve tool state
  const [curveStage, setCurveStage] = useState([]); // [p1, p2] so far, on 3rd click we commit
  // polygon tool state
  const [polyPts, setPolyPts] = useState([]);
  const skipBlurRef = useRef(false);
  const CW = 1200, CH = 680;

  // Init raster canvas
  useEffect(() => {
    const c = document.createElement('canvas');
    c.width = CW; c.height = CH;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CW, CH);
    rasterRef.current = c;
  }, []);

  // History (vector + raster snapshot). Kept in a ref so async callers
  // (image onload, timers, the window mouseup) never push onto a stale index.
  const histRef = useRef(initialHistory({ objects: [], rasterData: null }));
  const saveHistory = useCallback((objs, rasterData) => {
    histRef.current = pushHistory(histRef.current, { objects: objs, rasterData });
  }, []);
  const loadHistory = (snap) => {
    setObjects(JSON.parse(JSON.stringify(snap.objects)));
    setSelectedId(null); setEditingTextId(null); setFloatSel(null); setSelection(null);
    showRaster(snap.rasterData);
  };
  const stepTo = (delta) => {
    const next = stepHistory(histRef.current, delta);
    if (next === histRef.current) return;
    histRef.current = next;
    loadHistory(next.stack[next.idx]);
  };
  const undo = () => stepTo(-1);
  const redo = () => stepTo(1);
  const uid = () => `o${Date.now()}_${Math.random().toString(36).slice(2,6)}`;

  // Latest handlers/state for the window listeners below (assigned before
  // render returns, so they never call a stale closure).
  const r = useRef({});

  // Capture phase + stopPropagation: Paint's shortcuts (Ctrl+Z, Delete, …)
  // must not also reach the Content Editor's window handler behind the modal.
  useEffect(() => {
    const fn = (e) => {
      const c = r.current;
      if (c.editingTextId) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      const mod = e.ctrlKey || e.metaKey;
      const key = (e.key || '').toLowerCase();
      const handle = (action) => { e.preventDefault(); e.stopPropagation(); action(); };
      if ((e.key === 'Delete' || e.key === 'Backspace') && c.selectedId) handle(c.deleteObj);
      else if (mod && key === 'z' && !e.shiftKey) handle(c.undo);
      else if (mod && (key === 'y' || (e.shiftKey && key === 'z'))) handle(c.redo);
      else if (mod && key === 'c') handle(c.copySelection);
      else if (mod && key === 'x') handle(c.cutSelection);
      else if (mod && key === 'v') handle(c.pasteClipboard);
      else if (mod && key === 'a') handle(c.selectAll);
      else if (e.key === 'Escape') {
        handle(() => {
          c.commitFloatingSelection();
          setCurveStage([]); setPolyPts([]); setSelection(null);
        });
      } else if (e.key === 'Enter' && c.polyPts.length >= 3) handle(c.commitPolygon);
    };
    window.addEventListener('keydown', fn, true);
    return () => window.removeEventListener('keydown', fn, true);
  }, []);

  // Finish a stroke/drag/spray even when the mouse is released outside the canvas.
  useEffect(() => {
    const up = () => r.current.onStageUp();
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
      if (airbrushTimerRef.current) clearInterval(airbrushTimerRef.current);
    };
  }, []);

  // Transformer sync
  useEffect(() => {
    if (!trRef.current || !stageRef.current) return;
    if (selectedId && !editingTextId) {
      const node = stageRef.current.findOne('#'+selectedId);
      if (node) { trRef.current.nodes([node]); trRef.current.getLayer()?.batchDraw(); }
      else trRef.current.nodes([]);
    } else trRef.current.nodes([]);
  }, [selectedId, editingTextId, objects]);

  // Marching ants animation for selection (a few frames a second — every tick re-renders Paint)
  useEffect(() => {
    if (!selection && !floatSel) return undefined;
    let off = 0;
    const timer = setInterval(() => {
      off = (off + 1) % 8;
      setSelection(s => s ? { ...s, dashOffset: off } : s);
      setFloatSel(f => f ? { ...f, dashOffset: off } : f);
    }, MARCH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [!!selection, !!floatSel]);

  const getPos = () => {
    const p = stageRef.current?.getRelativePointerPosition();
    return p ? { x: p.x, y: p.y } : { x: 0, y: 0 };
  };

  // ── Render the drawing at 1:1 without editor chrome (text-box borders,
  //    placeholders, transformer handles, marquees, previews) ──
  const captureStage = (pixelRatio) => {
    const stage = stageRef.current;
    const hidden = [...stage.find(`.${OVERLAY}`), trRef.current].filter(n => n && n.visible());
    hidden.forEach(n => n.visible(false));
    const prevSX = stage.scaleX(), prevSY = stage.scaleY();
    stage.scale({ x: 1, y: 1 });
    try {
      return stage.toCanvas({ pixelRatio, x: 0, y: 0, width: CW, height: CH });
    } finally {
      stage.scale({ x: prevSX, y: prevSY });
      hidden.forEach(n => n.visible(true));
    }
  };

  // ── Flatten stage to a raster canvas (composites raster + vectors at 1:1 scale) ──
  const flattenStage = () => {
    const c = document.createElement('canvas');
    c.width = CW; c.height = CH;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, CW, CH);
    try {
      ctx.drawImage(captureStage(1), 0, 0);
    } catch (e) { console.error(e); }
    return c;
  };

  // Vectors were flattened into the raster canvas: show it, drop them, one history step.
  const bakeRaster = () => {
    const src = rasterRef.current.toDataURL();
    showRaster(src);
    setObjects([]); setSelectedId(null);
    saveHistory([], src);
  };

  // ── Eyedropper ──
  const pickColor = (x, y) => {
    const c = flattenStage();
    const ctx = c.getContext('2d');
    const data = ctx.getImageData(Math.round(x), Math.round(y), 1, 1).data;
    const hex = rgbToHex(data[0], data[1], data[2]);
    setStrokeColor(hex);
    setTool('select');
  };

  // ── Flood fill ──
  const doFloodFill = (x, y, color) => {
    const px = Math.round(x), py = Math.round(y);
    if (px < 0 || px >= CW || py < 0 || py >= CH) return;
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, CW, CH);
    ctx.drawImage(flattenStage(), 0, 0);
    const img = ctx.getImageData(0, 0, CW, CH);
    floodFillImageData(img, px, py, hexToRgba(color));
    ctx.putImageData(img, 0, 0);
    bakeRaster();
  };

  // ── Airbrush ──
  const airbrushBakeFirst = () => {
    // Flatten all vectors + raster into the raster layer so spray draws on top of everything
    const flat = flattenStage();
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, CW, CH);
    ctx.drawImage(flat, 0, 0);
    setObjects([]); setSelectedId(null);
  };
  const airbrushTick = (pos) => {
    const raster = rasterRef.current;
    const ctx = raster.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = strokeColor;
    const density = 12 + strokeWidth * 3;
    const radius = Math.max(6, strokeWidth * 4);
    for (let i = 0; i < density; i++) {
      const a = Math.random() * Math.PI * 2;
      const r2 = Math.random() * radius;
      const dx = Math.cos(a) * r2, dy = Math.sin(a) * r2;
      ctx.fillRect(Math.round(pos.x + dx), Math.round(pos.y + dy), 1, 1);
    }
    const img = new window.Image();
    img.onload = () => setRasterImg(img);
    img.src = raster.toDataURL();
  };

  // ── Rectangular selection: cut pixels from stage → floating selection
  //    (or, for Cut, just remove them) ──
  const commitRectSelection = (x, y, w, h, { asFloat = true } = {}) => {
    if (w < 3 || h < 3) { setSelection(null); return; }
    const stageCanvas = flattenStage();
    const ix = Math.max(0, Math.round(x)), iy = Math.max(0, Math.round(y));
    const iw = Math.min(CW - ix, Math.round(w)), ih = Math.min(CH - iy, Math.round(h));
    if (iw < 1 || ih < 1) { setSelection(null); return; }
    const frag = document.createElement('canvas');
    frag.width = iw; frag.height = ih;
    frag.getContext('2d').drawImage(stageCanvas, ix, iy, iw, ih, 0, 0, iw, ih);
    // Everything becomes raster, with the lifted region left white
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, CW, CH);
    ctx.drawImage(stageCanvas, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(ix, iy, iw, ih);
    const bgSrc = rasterRef.current.toDataURL();
    showRaster(bgSrc);
    setObjects([]); setSelectedId(null); setSelection(null);
    if (!asFloat) { saveHistory([], bgSrc); return; }
    const fragSrc = frag.toDataURL();
    const fimg = new window.Image();
    fimg.onload = () => setFloatSel({ x: ix, y: iy, w: iw, h: ih, image: fimg, dataURL: fragSrc, dashOffset: 0 });
    fimg.src = fragSrc;
  };

  // Synchronous so a click that commits and then fills/sprays sees the result.
  const commitFloatingSelection = () => {
    if (!floatSel) return;
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(floatSel.image, floatSel.x, floatSel.y, floatSel.w, floatSel.h);
    const src = rasterRef.current.toDataURL();
    showRaster(src);
    setFloatSel(null);
    saveHistory(objects, src);
  };

  const copySelection = () => {
    if (floatSel) { clipboardRef.current = { dataURL: floatSel.dataURL, w: floatSel.w, h: floatSel.h }; return; }
    if (selection) {
      const stageCanvas = flattenStage();
      const frag = document.createElement('canvas');
      frag.width = selection.w; frag.height = selection.h;
      frag.getContext('2d').drawImage(stageCanvas, selection.x, selection.y, selection.w, selection.h, 0, 0, selection.w, selection.h);
      clipboardRef.current = { dataURL: frag.toDataURL(), w: selection.w, h: selection.h };
    }
  };
  const cutSelection = () => {
    copySelection();
    if (floatSel) { setFloatSel(null); saveHistory(objects, rasterRef.current.toDataURL()); }
    else if (selection) commitRectSelection(selection.x, selection.y, selection.w, selection.h, { asFloat: false });
  };
  const pasteClipboard = () => {
    if (!clipboardRef.current) return;
    if (floatSel) commitFloatingSelection();
    const img = new window.Image();
    img.onload = () => setFloatSel({ x: 20, y: 20, w: clipboardRef.current.w, h: clipboardRef.current.h, image: img, dataURL: clipboardRef.current.dataURL, dashOffset: 0 });
    img.src = clipboardRef.current.dataURL;
    setTool('select');
  };
  const selectAll = () => { setSelection({ x: 0, y: 0, w: CW, h: CH, dashOffset: 0 }); setTool('rectSelect'); };

  // ── Stage handlers ──
  const onStageDown = (e) => {
    const target = e.target;
    const stage = target.getStage();
    const isBg = target === stage || ['bg','raster','selbg'].includes(target.attrs?.name);
    const pos = getPos();

    // Magnifier: click to zoom in, right-click to zoom out (handled via button)
    // Magnifier zooms in (right-click zooms out); minifier zooms out.
    if (tool === 'magnifier' || tool === 'minifier') {
      const zoomOut = tool === 'minifier' || e.evt?.button === 2;
      setZoom(zoomStep(zoom, zoomOut ? -1 : 1));
      return;
    }

    // If there's a floating selection and click is outside it → commit it
    if (floatSel && (pos.x < floatSel.x || pos.x > floatSel.x + floatSel.w || pos.y < floatSel.y || pos.y > floatSel.y + floatSel.h)) {
      commitFloatingSelection();
    }

    // Eyedropper
    if (tool === 'eyedropper') { pickColor(pos.x, pos.y); return; }

    // Fill
    // Clicking a closed shape/polygon fills that shape (it stays editable);
    // anywhere else is a pixel flood fill.
    if (tool === 'fill') {
      const color = fillColor || strokeColor;
      const hitId = target.id?.() || target.parent?.id?.();
      const hit = objects.find(o => o.id === hitId);
      if (isVectorFillable(hit)) {
        const n = objects.map(o => o.id === hit.id ? withVectorFill(o, color) : o);
        setObjects(n); saveHistory(n, rasterImg?.src);
      } else {
        doFloodFill(pos.x, pos.y, color);
      }
      return;
    }

    // Rectangular select
    if (tool === 'rectSelect') {
      setSelection({ x: pos.x, y: pos.y, w: 0, h: 0, dashOffset: 0, creating: true, startX: pos.x, startY: pos.y });
      setIsDrawing(true); setDrawStart(pos);
      return;
    }

    // Curve: 3-click state machine
    if (tool === 'curve') {
      const n = [...curveStage, pos];
      if (n.length === 3) {
        // commit quadratic curve
        const [p1, p2, ctrl] = n;
        const obj = { id:uid(), type:'curve', points:[p1.x,p1.y, ctrl.x,ctrl.y, p2.x,p2.y], stroke:strokeColor, strokeWidth, draggable:true };
        const nx = [...objects, obj];
        setObjects(nx); saveHistory(nx, rasterImg?.src); setCurveStage([]);
      } else setCurveStage(n);
      return;
    }

    // Polygon: click to add, double-click/Enter to close
    if (tool === 'polygon') {
      setPolyPts(prev => [...prev, pos]);
      return;
    }

    // Airbrush: start spraying
    if (tool === 'airbrush') {
      setIsDrawing(true);
      airbrushBakeFirst();
      airbrushTick(pos);
      airbrushTimerRef.current = setInterval(() => { const p = getPos(); airbrushTick(p); }, AIRBRUSH_INTERVAL_MS);
      return;
    }

    if (!isBg && tool === 'select') {
      // Clicking on another object while editing text → commit the text first
      if (editingTextId && target.id?.() !== editingTextId && target.parent?.id?.() !== editingTextId) commitText();
      return;
    }
    if (isBg) { if (editingTextId) commitText(); setSelectedId(null); }
    if (tool === 'select') return;

    // Text
    if (tool === 'text') {
      setIsDrawing(true); setDrawStart(pos);
      setObjects(prev => [...prev, { id:uid(), type:'text', x:pos.x, y:pos.y, text:'', fontSize, fontFamily,
        fontStyle:`${fontItalic?'italic ':''}${fontBold?'bold':'normal'}`, textDecoration:fontUnderline?'underline':'',
        align:textAlign, fill:strokeColor, draggable:true, width:4, height:4, isNew:true }]);
      return;
    }

    // Freehand
    if (['pen','marker','highlighter','eraser','brush'].includes(tool)) {
      setIsDrawing(true);
      let sw = strokeWidth;
      let color = strokeColor;
      let opacity = 1;
      let cap = 'round', join = 'round';
      if (tool==='marker') sw = Math.max(strokeWidth*3, 8);
      else if (tool==='highlighter') { sw = Math.max(strokeWidth*5, 18); opacity = 0.35; }
      else if (tool==='eraser') { sw = Math.max(strokeWidth*4, 20); color = '#ffffff'; }
      else if (tool==='brush') {
        sw = Math.max(strokeWidth*2, 6);
        if (brushTip==='square') { cap = 'butt'; join = 'miter'; }
      }
      setObjects(prev => [...prev, { id:uid(), type:'freehand', points:[pos.x,pos.y], stroke:color, strokeWidth:sw, opacity, cap, join, tip:brushTip, draggable:false }]);
      return;
    }

    // Lines / arrows
    if (['line','arrow','doubleArrow'].includes(tool)) {
      setIsDrawing(true); setDrawStart(pos);
      setObjects(prev => [...prev, { id:uid(), type:tool, points:[pos.x,pos.y,pos.x,pos.y], stroke:strokeColor, strokeWidth, draggable:true }]);
      return;
    }

    // Shape
    if (tool === 'shape') {
      setIsDrawing(true); setDrawStart(pos);
      const appliedFill = fillMode === 'outline' ? '' : (fillColor || strokeColor);
      const appliedStroke = fillMode === 'fill' ? '' : strokeColor;
      setObjects(prev => [...prev, { id:uid(), type:'shape', shapeType, x:pos.x, y:pos.y, width:1, height:1, stroke:appliedStroke, strokeWidth, fill:appliedFill, fillMode, draggable:true }]);
    }
  };

  const onStageMove = () => {
    const pos = getPos();
    setCursor({ x: Math.round(pos.x), y: Math.round(pos.y) });

    // Floating selection drag
    if (floatSel?.dragging) {
      setFloatSel(f => ({ ...f, x: pos.x - f.dragOffX, y: pos.y - f.dragOffY }));
      return;
    }

    if (!isDrawing) return;

    // Rectangular selection drawing
    if (tool === 'rectSelect' && selection) {
      const x0 = selection.startX, y0 = selection.startY;
      setSelection(s => ({ ...s, x: Math.min(x0, pos.x), y: Math.min(y0, pos.y), w: Math.abs(pos.x - x0), h: Math.abs(pos.y - y0) }));
      return;
    }

    setObjects(prev => {
      const last = prev[prev.length-1]; if (!last) return prev;
      const cp = [...prev];
      if (last.type==='freehand') cp[cp.length-1] = { ...last, points:[...last.points,pos.x,pos.y] };
      else if (['line','arrow','doubleArrow'].includes(last.type)) cp[cp.length-1] = { ...last, points:[last.points[0],last.points[1],pos.x,pos.y] };
      else if (last.type==='shape'&&drawStart) cp[cp.length-1] = { ...last, width:pos.x-drawStart.x, height:pos.y-drawStart.y };
      else if (last.type==='text'&&last.isNew&&drawStart) cp[cp.length-1] = { ...last, width:Math.max(20,pos.x-drawStart.x), height:Math.max(20,pos.y-drawStart.y) };
      return cp;
    });
  };

  const onStageUp = () => {
    // Stop airbrush
    if (airbrushTimerRef.current) {
      clearInterval(airbrushTimerRef.current); airbrushTimerRef.current = null;
      setIsDrawing(false);
      bakeRaster();
      return;
    }

    // Commit floating selection drag
    if (floatSel?.dragging) { setFloatSel(f => ({ ...f, dragging: false })); return; }

    if (!isDrawing) return;
    setIsDrawing(false); setDrawStart(null);

    // Commit rect selection
    if (tool === 'rectSelect' && selection) {
      const { x, y, w, h } = selection;
      if (w > 2 && h > 2) commitRectSelection(x, y, w, h);
      else setSelection(null);
      return;
    }

    const last = objects[objects.length-1];
    if (last?.type==='text'&&last.isNew) {
      const w = Math.max(80,last.width||200), h = Math.max(30,last.height||30);
      const fin = objects.map((o,i)=>i===objects.length-1?{...o,width:w,height:h,isNew:false}:o);
      setObjects(fin); saveHistory(fin, rasterImg?.src);
      skipBlurRef.current = true; setTimeout(()=>{skipBlurRef.current=false;},400);
      setSelectedId(last.id); setEditingTextId(last.id);
    } else if (last?.type==='shape') {
      // Normalize negative width/height so drag/transform math is consistent
      const nw = Math.abs(last.width || 1);
      const nh = Math.abs(last.height || 1);
      const nx = (last.width || 0) < 0 ? last.x + (last.width || 0) : last.x;
      const ny = (last.height || 0) < 0 ? last.y + (last.height || 0) : last.y;
      const fin = objects.map((o,i)=>i===objects.length-1?{...o,x:nx,y:ny,width:Math.max(4,nw),height:Math.max(4,nh)}:o);
      setObjects(fin); saveHistory(fin, rasterImg?.src); setSelectedId(last.id); setTool('select');
    } else if (last?.type==='line'||last?.type==='arrow'||last?.type==='doubleArrow') {
      saveHistory(objects, rasterImg?.src); setSelectedId(last.id); setTool('select');
    } else saveHistory(objects, rasterImg?.src);
  };

  // Polygon commit on double-click
  const commitPolygon = () => {
    // A double-click lands two extra clicks on the closing point — drop them.
    const clean = dedupePolygonPoints(polyPts);
    if (clean.length < 3) { setPolyPts([]); return; }
    const pts = clean.flatMap(p => [p.x, p.y]);
    const appliedFill = fillMode === 'outline' ? '' : (fillColor || strokeColor);
    const appliedStroke = fillMode === 'fill' ? '' : strokeColor;
    const obj = { id:uid(), type:'polygon', points:pts, stroke:appliedStroke, strokeWidth, fill:appliedFill, draggable:true };
    const nx = [...objects, obj];
    setObjects(nx); saveHistory(nx, rasterImg?.src); setPolyPts([]); setTool('select');
  };

  // Text editing
  const startTextEdit = (id) => { skipBlurRef.current=true; setTimeout(()=>{skipBlurRef.current=false;},400); setEditingTextId(id); setSelectedId(id); };
  const commitText = () => {
    if (!editingTextId||skipBlurRef.current) return;
    const obj = objects.find(o=>o.id===editingTextId);
    if (obj&&(!obj.text||!obj.text.trim())) { const n=objects.filter(o=>o.id!==editingTextId); setObjects(n); saveHistory(n, rasterImg?.src); }
    else saveHistory(objects, rasterImg?.src);
    setEditingTextId(null);
  };
  useEffect(() => { if (editingTextId && textRef.current) { textRef.current.focus(); textRef.current.select(); } }, [editingTextId]);

  // Image import
  const onFileChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const id=uid(); let w=img.width,h=img.height;
        if(w>CW*0.6){h=h*CW*0.6/w;w=CW*0.6;} if(h>CH*0.6){w=w*CH*0.6/h;h=CH*0.6;}
        setImages(prev=>({...prev,[id]:img}));
        const n=[...objects,{id,type:'image',x:50,y:50,width:w,height:h,draggable:true,src:reader.result}];
        setObjects(n); saveHistory(n, rasterImg?.src); setSelectedId(id); setTool('select');
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file); e.target.value='';
  };
  useEffect(() => { objects.forEach(o=>{if(o.type==='image'&&o.src&&!images[o.id]){const img=new window.Image();img.onload=()=>setImages(prev=>({...prev,[o.id]:img}));img.src=o.src;}}); }, [objects, images]);

  // Drag/Transform
  const onDragEnd = (id,e) => { const n=objects.map(o=>o.id===id?{...o,x:e.target.x(),y:e.target.y()}:o); setObjects(n); saveHistory(n, rasterImg?.src); };
  // For points-based shapes (line/arrow/doubleArrow/curve/polygon), translate the points array by the drag delta and reset the node's position to 0.
  // This keeps `obj.points` as the source of truth and avoids snap-back on re-render.
  const onDragEndPoints = (id, e) => {
    const dx = e.target.x(), dy = e.target.y();
    if (dx === 0 && dy === 0) return;
    const n = objects.map(o => {
      if (o.id !== id || !Array.isArray(o.points)) return o;
      const np = [];
      for (let i = 0; i < o.points.length; i += 2) { np.push(o.points[i] + dx, o.points[i+1] + dy); }
      return { ...o, points: np };
    });
    e.target.position({ x: 0, y: 0 });
    setObjects(n); saveHistory(n, rasterImg?.src);
  };
  // Resize/rotate of box shapes: scale the stored size (a triangle's node is only
  // min(w,h) wide) and keep rotation, which is rendered from obj.rotation.
  const onTransformEnd = (id, e) => {
    const nd = e.target;
    const t = { x: nd.x(), y: nd.y(), scaleX: nd.scaleX(), scaleY: nd.scaleY(), rotation: nd.rotation() };
    nd.scaleX(1); nd.scaleY(1);
    const n = objects.map(o => o.id === id
      ? resizedBox({ ...o, width: o.width || nd.width(), height: o.height || nd.height() }, t)
      : o);
    setObjects(n); saveHistory(n, rasterImg?.src);
  };
  // Lines/arrows/curves/polygons store absolute points: bake the node's
  // transform into them and reset the node, so resize/rotate actually stick.
  const onTransformEndPoints = (id, e) => {
    const nd = e.target;
    const tf = nd.getTransform().copy();
    nd.position({ x: 0, y: 0 }); nd.scale({ x: 1, y: 1 }); nd.rotation(0);
    const n = objects.map(o => (o.id === id && Array.isArray(o.points))
      ? { ...o, points: mapPoints(o.points, p => tf.point(p)) }
      : o);
    setObjects(n); saveHistory(n, rasterImg?.src);
  };

  // Actions
  const deleteObj = () => { if(!selectedId) return; const n=objects.filter(o=>o.id!==selectedId); setObjects(n); saveHistory(n, rasterImg?.src); setSelectedId(null); };
  const duplicateObj = () => { const obj=objects.find(o=>o.id===selectedId); if(!obj) return; const id=uid(); const dup={...JSON.parse(JSON.stringify(obj)),id,x:(obj.x||0)+20,y:(obj.y||0)+20}; const n=[...objects,dup]; setObjects(n); saveHistory(n, rasterImg?.src); setSelectedId(id); if(obj.type==='image'&&images[obj.id]) setImages(prev=>({...prev,[id]:prev[obj.id]})); };
  const clearAll = () => {
    setObjects([]); setRasterImg(null); setSelectedId(null); setEditingTextId(null); setFloatSel(null); setSelection(null);
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, CW, CH);
    saveHistory([], null);
  };
  const handleSave = () => {
    setSaving(true); setSelectedId(null); setEditingTextId(null);
    if (floatSel) commitFloatingSelection();
    setTimeout(() => {
      try {
        onSave(captureStage(2).toDataURL());
      } catch (er) { console.error(er); }
      setSaving(false);
    }, 300);
  };

  // Render objects
  const renderObj = (obj) => {
    const click = () => { if(tool==='select') setSelectedId(obj.id); };
    const dp = obj.draggable ? { onDragEnd:e=>onDragEnd(obj.id,e), onClick:click, onTap:click } : {};
    // For points-based shapes (line/arrow/doubleArrow/curve/polygon), use points-translation handler
    const dpPts = obj.draggable ? { onDragEnd:e=>onDragEndPoints(obj.id,e), onClick:click, onTap:click, onTransformEnd:e=>onTransformEndPoints(obj.id,e) } : {};

    if (obj.type==='freehand') {
      const dashArray = obj.tip === 'slash' ? [obj.strokeWidth*2, obj.strokeWidth] : undefined;
      return <Line key={obj.id} points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} opacity={obj.opacity} lineCap={obj.cap||'round'} lineJoin={obj.join||'round'} tension={0.5} dash={dashArray} listening={false} />;
    }
    if (obj.type==='line') return <Line key={obj.id} id={obj.id} points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} draggable hitStrokeWidth={20} {...dpPts} />;
    if (obj.type==='arrow') return <Arrow key={obj.id} id={obj.id} points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} fill={obj.stroke} pointerLength={12} pointerWidth={10} draggable hitStrokeWidth={20} {...dpPts} />;
    if (obj.type==='doubleArrow') return (<Group key={obj.id} id={obj.id} draggable {...dpPts}><Arrow points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} fill={obj.stroke} pointerLength={12} pointerWidth={10} /><Arrow points={[obj.points[2],obj.points[3],obj.points[0],obj.points[1]]} stroke={obj.stroke} strokeWidth={obj.strokeWidth} fill={obj.stroke} pointerLength={12} pointerWidth={10} /></Group>);
    if (obj.type==='curve') {
      const [x1,y1,cx,cy,x2,y2] = obj.points;
      const d = `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
      return <Path key={obj.id} id={obj.id} data={d} stroke={obj.stroke} strokeWidth={obj.strokeWidth} draggable hitStrokeWidth={20} {...dpPts} />;
    }
    if (obj.type==='polygon') return <Line key={obj.id} id={obj.id} points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} fill={obj.fill||'rgba(0,0,0,0.001)'} closed draggable hitStrokeWidth={20} {...dpPts} />;
    if (obj.type==='text') {
      const hidden = editingTextId===obj.id;
      const bw = obj.width||200, bh = obj.height||(obj.fontSize*2);
      return (<Group key={obj.id} id={obj.id} x={obj.x} y={obj.y} width={bw} height={bh} rotation={obj.rotation||0} draggable onDragEnd={e=>onDragEnd(obj.id,e)} onClick={click} onTap={click} onDblClick={()=>startTextEdit(obj.id)} onDblTap={()=>startTextEdit(obj.id)} onTransformEnd={e=>onTransformEnd(obj.id,e)}>
        <Rect name={OVERLAY} width={bw} height={bh} stroke={selectedId===obj.id?'#3b82f6':'#94a3b8'} strokeWidth={1} dash={[4,4]} fill="rgba(255,255,255,0.01)" opacity={selectedId===obj.id||editingTextId===obj.id?1:0.35} />
        {!hidden && <Text name={obj.text ? undefined : OVERLAY} x={6} y={6} text={obj.text||(obj.isNew?'':'Double-click to type')} fontSize={obj.fontSize} fontFamily={obj.fontFamily} fontStyle={obj.fontStyle} textDecoration={obj.textDecoration||''} align={obj.align||'left'} fill={obj.fill} width={bw-12} height={bh-12} opacity={obj.text?1:0.35} listening={false} />}
      </Group>);
    }
    if (obj.type==='image'&&images[obj.id]) return <KImage key={obj.id} id={obj.id} x={obj.x} y={obj.y} image={images[obj.id]} width={obj.width} height={obj.height} rotation={obj.rotation||0} draggable onDragEnd={e=>onDragEnd(obj.id,e)} onClick={click} onTap={click} onTransformEnd={e=>onTransformEnd(obj.id,e)} />;
    if (obj.type==='shape') {
      const st=obj.shapeType,w=Math.abs(obj.width||80),h=Math.abs(obj.height||60);
      const ox=(obj.width||0)<0?obj.x+(obj.width||0):obj.x, oy=(obj.height||0)<0?obj.y+(obj.height||0):obj.y;
      // Use invisible-but-hittable fill when no fill is set (outline mode) so clicks/drag work on interior
      const hitFill = obj.fill ? obj.fill : 'rgba(0,0,0,0.001)';
      const fp={stroke:obj.stroke||undefined,strokeWidth:obj.strokeWidth,fill:hitFill,rotation:obj.rotation||0,draggable:true,...dp,onTransformEnd:e=>onTransformEnd(obj.id,e)};
      // Centre-drawn Konva shapes get a negative offset so node.x()/y() is the
      // top-left corner — otherwise they jump by w/2,h/2 on every drag/resize.
      const cp = isCenterAnchored(st) ? { x: ox, y: oy, offsetX: -w/2, offsetY: -h/2 } : null;
      if(st==='rect') return <Rect key={obj.id} id={obj.id} x={ox} y={oy} width={w} height={h} {...fp} />;
      if(st==='roundRect') return <Rect key={obj.id} id={obj.id} x={ox} y={oy} width={w} height={h} cornerRadius={Math.min(14,w/4,h/4)} {...fp} />;
      if(st==='ellipse') return <Ellipse key={obj.id} id={obj.id} {...cp} radiusX={w/2} radiusY={h/2} {...fp} />;
      if(st==='circle'){const rad=Math.min(w,h)/2; return <Circle key={obj.id} id={obj.id} {...cp} radius={rad} {...fp} />;}
      if(st==='triangle') return <RegularPolygon key={obj.id} id={obj.id} {...cp} sides={3} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='diamond') return <RegularPolygon key={obj.id} id={obj.id} {...cp} sides={4} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='star') return <Star key={obj.id} id={obj.id} {...cp} numPoints={5} innerRadius={Math.min(w,h)/4} outerRadius={Math.min(w,h)/2} {...fp} />;
      if(st==='hexagon') return <RegularPolygon key={obj.id} id={obj.id} {...cp} sides={6} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='pentagon') return <RegularPolygon key={obj.id} id={obj.id} {...cp} sides={5} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='octagon') return <RegularPolygon key={obj.id} id={obj.id} {...cp} sides={8} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='trapezoid') return <Line key={obj.id} id={obj.id} x={ox} y={oy} points={[w*0.15,0,w*0.85,0,w,h,0,h]} closed {...fp} />;
      if(st==='cylinder') return (<Group key={obj.id} id={obj.id} x={ox} y={oy} width={w} height={h} rotation={obj.rotation||0} draggable {...dp} onTransformEnd={e=>onTransformEnd(obj.id,e)}>
        <Rect width={w} height={h} fill="rgba(0,0,0,0.001)" listening={true} />
        <Ellipse x={w/2} y={h*0.1} radiusX={w/2} radiusY={h*0.1} stroke={obj.stroke||undefined} strokeWidth={obj.strokeWidth} fill={obj.fill||undefined} />
        <Rect x={0} y={h*0.1} width={w} height={h*0.8} stroke="transparent" fill={obj.fill||undefined} />
        <Line points={[0,h*0.1,0,h*0.9]} stroke={obj.stroke||undefined} strokeWidth={obj.strokeWidth} />
        <Line points={[w,h*0.1,w,h*0.9]} stroke={obj.stroke||undefined} strokeWidth={obj.strokeWidth} />
        <Ellipse x={w/2} y={h*0.9} radiusX={w/2} radiusY={h*0.1} stroke={obj.stroke||undefined} strokeWidth={obj.strokeWidth} fill={obj.fill||undefined} />
      </Group>);
      if(st==='cloud'){const cx2=w/2,cy2=h/2,rx2=w/2,ry2=h/2;const pts=[];for(let i=0;i<12;i++){const a=i*Math.PI*2/12;const r2=i%2===0?1:0.75;pts.push(cx2+rx2*r2*Math.cos(a),cy2+ry2*r2*Math.sin(a));}return <Line key={obj.id} id={obj.id} x={ox} y={oy} points={pts} closed tension={0.4} {...fp} />;}
      return <Rect key={obj.id} id={obj.id} x={ox} y={oy} width={w} height={h} {...fp} />;
    }
    return null;
  };

  // Closing with an unsaved drawing throws it away — ask first. Joiners of a
  // live room (no onSave) lose nothing: the presenter still has the canvas.
  const hasUnsavedDrawing = Boolean(onSave) && hasDrawing({ objects, raster: rasterImg?.src, floating: floatSel });
  const requestClose = async () => {
    if (hasUnsavedDrawing) {
      const discard = await confirmDialog({
        title: 'Discard this drawing?',
        message: "Your drawing hasn't been saved to the section. If you close Paint now it will be permanently deleted.",
        confirmLabel: 'Discard drawing',
        cancelLabel: 'Keep drawing',
        danger: true,
      });
      if (!discard) return;
    }
    onClose?.();
  };
  // Closing or refreshing the browser tab: browsers only allow their own generic "leave site?" prompt.
  useEffect(() => {
    if (!hasUnsavedDrawing) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedDrawing]);

  r.current = {
    editingTextId, selectedId, polyPts, deleteObj, undo, redo, copySelection, cutSelection,
    pasteClipboard, selectAll, commitFloatingSelection, commitPolygon, onStageUp,
  };

  const editObj = editingTextId ? objects.find(o=>o.id===editingTextId) : null;
  const TB = ({id,active,onClick,children,title}) => (<button onClick={onClick} className={`h-8 px-2 rounded-md flex items-center justify-center gap-1 text-xs font-medium transition ${active?'bg-[#34A853] text-white shadow-sm':'text-gray-600 hover:bg-gray-100'}`} title={title} data-testid={`draw-tool-${id}`}>{children}</button>);

  // Curve preview line while picking points
  const curvePreview = curveStage.length >= 1 ? (() => {
    if (curveStage.length === 1) {
      return <Line name={OVERLAY} points={[curveStage[0].x, curveStage[0].y, cursor.x, cursor.y]} stroke={strokeColor} strokeWidth={strokeWidth} dash={[4,4]} listening={false} />;
    }
    if (curveStage.length === 2) {
      const [p1, p2] = curveStage;
      const d = `M ${p1.x} ${p1.y} Q ${cursor.x} ${cursor.y} ${p2.x} ${p2.y}`;
      return <Path name={OVERLAY} data={d} stroke={strokeColor} strokeWidth={strokeWidth} dash={[4,4]} listening={false} />;
    }
    return null;
  })() : null;

  // Polygon preview
  const polyPreview = polyPts.length >= 1 ? (() => {
    const pts = [...polyPts.flatMap(p => [p.x, p.y]), cursor.x, cursor.y];
    return <Line name={OVERLAY} points={pts} stroke={strokeColor} strokeWidth={strokeWidth} dash={[4,4]} listening={false} />;
  })() : null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center" data-testid="drawing-canvas-modal" onClick={requestClose}>
      <div className="bg-[#f0f0f0] rounded-xl shadow-xl w-[97vw] max-w-[1400px] h-[94vh] flex flex-col overflow-hidden" onClick={e=>e.stopPropagation()}>
        {/* ── Titlebar ── */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white border-b border-gray-200 flex-shrink-0">
          <span className="font-semibold text-gray-900 text-sm">Desky Paint</span>
          {collabRoom && (
            <CollabBadge roomId={collabRoom} peerCount={peerCount} status={collabStatus} className="ml-1" />
          )}
          {/* Go Live hidden for now — restore with startCollab + newRoomId import.
          {!collabRoom && (
            <button
              onClick={startCollab}
              data-testid="paint-start-collab"
              className="ml-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100">
              ● Go Live
            </button>
          )} */}

          {/* Zoom */}
          <select value={zoom} onChange={e=>setZoom(parseFloat(e.target.value))} className="text-[11px] h-6 px-1 rounded border border-gray-200 bg-white ml-1" data-testid="draw-zoom">
            {ZOOMS.map(z => <option key={z} value={z}>{Math.round(z*100)}%</option>)}
          </select>

          <div className="flex-1" />
          {onSave && <button onClick={handleSave} disabled={saving} className="font-semibold text-white px-5 py-1.5 rounded-lg bg-[#1A7F37] hover:bg-[#166B2E] disabled:opacity-50 text-xs" data-testid="draw-save">{saving?'Saving…':'Save to Section'}</button>}
          <button onClick={requestClose} className="text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100 ml-1 text-[18px]" data-testid="draw-close">&times;</button>
        </div>

        {/* ── Main toolbar ── */}
        <div className="flex items-center gap-1 px-2 py-1.5 bg-white border-b border-gray-200 flex-wrap">
          {/* Selection tools */}
          <div className="flex gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            <TB id="select" active={tool==='select'} onClick={()=>setTool('select')} title="Select/Move"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/></svg></TB>
            <TB id="rectSelect" active={tool==='rectSelect'} onClick={()=>setTool('rectSelect')} title="Rectangular Select (Ctrl+A all, Ctrl+C/X/V)"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"><rect x="3" y="3" width="18" height="18" rx="1"/></svg></TB>
            <TB id="eyedropper" active={tool==='eyedropper'} onClick={()=>setTool('eyedropper')} title="Eyedropper — pick color"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 22l5-5"/><path d="M7 17l5 5"/><path d="M11 13l7-7"/><path d="M14 6l4 4"/><path d="M16 3l5 5"/></svg></TB>
            <TB id="magnifier" active={tool==='magnifier'} onClick={()=>setTool('magnifier')} title="Magnifier — click to zoom in"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg></TB>
            <TB id="minifier" active={tool==='minifier'} onClick={()=>setTool('minifier')} title="Minifier — click to zoom out"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg></TB>
          </div>
          <div className="w-px h-7 bg-gray-200" />

          {/* Drawing tools */}
          <div className="flex gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            <TB id="pen" active={tool==='pen'} onClick={()=>setTool('pen')} title="Pencil"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg></TB>
            <TB id="brush" active={tool==='brush'} onClick={()=>setTool('brush')} title="Brush"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.06 11.9L16.96 4l3.48 3.48-7.9 7.9m-3.48-3.48l-3.48 3.48 3.48 3.48 3.48-3.48m-3.48-3.48l3.48 3.48"/></svg></TB>
            <TB id="airbrush" active={tool==='airbrush'} onClick={()=>setTool('airbrush')} title="Airbrush"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="17" cy="7" r="1"/><circle cx="19" cy="9" r="1"/><circle cx="21" cy="11" r="1"/><circle cx="14" cy="5" r="1"/><circle cx="16" cy="3" r="1"/><path d="M4 20L13 11"/><rect x="2" y="17" width="5" height="5" rx="1" transform="rotate(-45 4.5 19.5)"/></svg></TB>
            <TB id="marker" active={tool==='marker'} onClick={()=>setTool('marker')} title="Marker"><b className="text-sm">M</b></TB>
            <TB id="highlighter" active={tool==='highlighter'} onClick={()=>setTool('highlighter')} title="Highlighter"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" opacity=".4"><rect x="4" y="10" width="16" height="8" rx="2"/></svg></TB>
            <TB id="eraser" active={tool==='eraser'} onClick={()=>setTool('eraser')} title="Eraser"><span className="text-[14px]">⌫</span></TB>
          </div>
          <div className="w-px h-7 bg-gray-200" />

          {/* Line / curve / shape tools */}
          <div className="flex gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            <TB id="line" active={tool==='line'} onClick={()=>setTool('line')} title="Line"><svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none"><line x1="5" y1="19" x2="19" y2="5"/></svg></TB>
            <TB id="curve" active={tool==='curve'} onClick={()=>{setTool('curve'); setCurveStage([]);}} title="Curve — click 3 points"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 18 Q 9 3, 21 18"/></svg></TB>
            <TB id="arrow" active={tool==='arrow'} onClick={()=>setTool('arrow')} title="Arrow">→</TB>
            <TB id="doubleArrow" active={tool==='doubleArrow'} onClick={()=>setTool('doubleArrow')} title="Double Arrow">↔</TB>
            <TB id="polygon" active={tool==='polygon'} onClick={()=>{setTool('polygon'); setPolyPts([]);}} title="Polygon — click points, Enter/dbl-click to close"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12,2 22,8 18,21 6,21 2,8"/></svg></TB>
          </div>
          <div className="w-px h-7 bg-gray-200" />

          {/* Shapes dropdown */}
          <div className="relative">
            <button onClick={()=>setShowShapeMenu(!showShapeMenu)} className={`h-8 px-2.5 rounded-md flex items-center gap-1.5 text-xs font-medium border ${tool==='shape'?'bg-[#34A853] text-white border-[#34A853]':'text-gray-600 hover:bg-gray-100 border-gray-200 bg-white'}`} data-testid="draw-shapes-dropdown">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg> Shapes ▾
            </button>
            {showShapeMenu&&<div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 p-2.5 z-50 w-[260px]" data-testid="draw-shapes-menu">
              <p className="text-[11px] font-semibold text-gray-400 px-1 mb-1.5">Basic</p>
              <div className="grid grid-cols-5 gap-1 mb-2">{SHAPES.filter(s=>s.cat==='basic').map(s=>(<button key={s.id} onClick={()=>{setShapeType(s.id);setTool('shape');setShowShapeMenu(false);}} className={`h-12 rounded-lg flex flex-col items-center justify-center text-[7px] font-medium ${shapeType===s.id&&tool==='shape'?'bg-[#34A853]/10 text-[#34A853] ring-1 ring-[#34A853]':'text-gray-500 hover:bg-gray-50 border border-gray-100'}`} data-testid={`draw-shape-${s.id}`}><span className="text-[14px]">{{'rect':'▬','roundRect':'▢','ellipse':'⬭','circle':'●','triangle':'▲','diamond':'◆','star':'★'}[s.id]||'□'}</span><span className="mt-0.5">{s.label}</span></button>))}</div>
              <p className="text-[11px] font-semibold text-gray-400 px-1 mb-1.5">Flowchart</p>
              <div className="grid grid-cols-5 gap-1">{SHAPES.filter(s=>s.cat==='flow').map(s=>(<button key={s.id} onClick={()=>{setShapeType(s.id);setTool('shape');setShowShapeMenu(false);}} className={`h-12 rounded-lg flex flex-col items-center justify-center text-[7px] font-medium ${shapeType===s.id&&tool==='shape'?'bg-[#34A853]/10 text-[#34A853] ring-1 ring-[#34A853]':'text-gray-500 hover:bg-gray-50 border border-gray-100'}`} data-testid={`draw-shape-${s.id}`}><span className="text-[14px]">{{'hexagon':'⬡','pentagon':'⬠','octagon':'⯃','trapezoid':'⏢','cylinder':'⌗','cloud':'☁'}[s.id]||'□'}</span><span className="mt-0.5">{s.label}</span></button>))}</div>
            </div>}
          </div>
          <div className="w-px h-7 bg-gray-200" />

          <TB id="text" active={tool==='text'} onClick={()=>setTool('text')} title="Text — drag to create box"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="4,7 4,4 20,4 20,7"/><line x1="12" y1="4" x2="12" y2="20"/><line x1="8" y1="20" x2="16" y2="20"/></svg> Text</TB>
          <TB id="fill" active={tool==='fill'} onClick={()=>setTool('fill')} title="Paint Bucket"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.5 18.5l9-9 5 5-9 9a1.5 1.5 0 01-2.12 0l-2.88-2.88a1.5 1.5 0 010-2.12z"/><path d="M7.5 13.5L16 5l3 3"/></svg> Fill</TB>
          <div className="w-px h-7 bg-gray-200" />

          <button onClick={()=>fileRef.current?.click()} className="h-8 px-2.5 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100 flex items-center gap-1 border border-gray-200 bg-white" data-testid="draw-import-image"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg> Image</button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />

          {/* Text formatting */}
          {(tool==='text'||(selectedId&&objects.find(o=>o.id===selectedId)?.type==='text'))&&(
            <div className="flex items-center gap-1 ml-1 bg-gray-50 rounded-lg px-2 py-0.5 border border-gray-200">
              <select value={fontFamily} onChange={e=>{setFontFamily(e.target.value);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,fontFamily:e.target.value}:o);setObjects(n);}}} className="text-[11px] h-6 px-1 rounded border border-gray-200 bg-white" data-testid="draw-font-family">{FONTS.map(f=><option key={f} value={f}>{f}</option>)}</select>
              <select value={fontSize} onChange={e=>{setFontSize(+e.target.value);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,fontSize:+e.target.value}:o);setObjects(n);}}} className="text-[11px] h-6 w-12 px-1 rounded border border-gray-200 bg-white" data-testid="draw-font-size">{SIZES.map(s=><option key={s} value={s}>{s}</option>)}</select>
              <button onClick={()=>{const b=!fontBold;setFontBold(b);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,fontStyle:`${fontItalic?'italic ':''}${b?'bold':'normal'}`}:o);setObjects(n);}}} className={`w-6 h-6 rounded text-xs font-black ${fontBold?'bg-gray-800 text-white':'text-gray-400 hover:bg-gray-100'}`} data-testid="draw-bold">B</button>
              <button onClick={()=>{const i=!fontItalic;setFontItalic(i);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,fontStyle:`${i?'italic ':''}${fontBold?'bold':'normal'}`}:o);setObjects(n);}}} className={`w-6 h-6 rounded text-xs italic ${fontItalic?'bg-gray-800 text-white':'text-gray-400 hover:bg-gray-100'}`} data-testid="draw-italic">I</button>
              <button onClick={()=>{const u=!fontUnderline;setFontUnderline(u);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,textDecoration:u?'underline':''}:o);setObjects(n);}}} className={`w-6 h-6 rounded text-xs underline ${fontUnderline?'bg-gray-800 text-white':'text-gray-400 hover:bg-gray-100'}`} data-testid="draw-underline">U</button>
              <div className="w-px h-4 bg-gray-200" />
              {['left','center','right'].map(a=>(<button key={a} onClick={()=>{setTextAlign(a);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,align:a}:o);setObjects(n);}}} className={`w-6 h-6 rounded flex items-center justify-center ${textAlign===a?'bg-gray-800 text-white':'text-gray-400 hover:bg-gray-100'}`} data-testid={`draw-align-${a}`}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1={a==='right'?'9':'3'} y1="12" x2={a==='left'?'15':'21'} y2="12"/><line x1={a==='right'?'6':a==='center'?'4':'3'} y1="18" x2={a==='left'?'18':a==='center'?'20':'21'} y2="18"/></svg></button>))}
            </div>
          )}
          <div className="flex-1" />

          {/* Clipboard buttons */}
          <div className="flex gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            <button onClick={cutSelection} className="text-[11px] font-medium text-gray-600 px-2 py-1 rounded hover:bg-white" title="Cut (Ctrl+X)" data-testid="draw-cut"><Scissors className="w-3.5 h-3.5" aria-hidden="true" /></button>
            <button onClick={copySelection} className="text-[11px] font-medium text-gray-600 px-2 py-1 rounded hover:bg-white" title="Copy (Ctrl+C)" data-testid="draw-copy"><Copy className="w-3.5 h-3.5" aria-hidden="true" /></button>
            <button onClick={pasteClipboard} className="text-[11px] font-medium text-gray-600 px-2 py-1 rounded hover:bg-white" title="Paste (Ctrl+V)" data-testid="draw-paste"><ClipboardPaste className="w-3.5 h-3.5" aria-hidden="true" /></button>
          </div>

          {selectedId&&<div className="flex gap-1 bg-red-50 rounded-lg px-1 py-0.5 border border-red-100 ml-1"><button onClick={duplicateObj} className="text-[11px] font-medium text-gray-600 px-2 py-1 rounded hover:bg-gray-100" data-testid="draw-duplicate">Dup</button><button onClick={deleteObj} className="text-[11px] font-medium text-red-500 px-2 py-1 rounded hover:bg-red-100" data-testid="draw-delete">Del</button></div>}
          <button onClick={undo} className="text-gray-500 p-1.5 rounded hover:bg-gray-100" data-testid="draw-undo" title="Undo (Ctrl+Z)"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1,4 1,10 7,10"/><path d="M3.51 15a9 9 0 105.64-11.36L1 10"/></svg></button>
          <button onClick={redo} className="text-gray-500 p-1.5 rounded hover:bg-gray-100" data-testid="draw-redo" title="Redo (Ctrl+Y)"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23,4 23,10 17,10"/><path d="M20.49 15a9 9 0 11-5.64-11.36L23 10"/></svg></button>
          <button onClick={clearAll} className="text-red-400 p-1.5 rounded hover:bg-red-50" data-testid="draw-clear" title="Clear canvas"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg></button>
        </div>

        {/* ── Properties strip (Colors + Line widths + Brush tips + Fill mode) ── */}
        <div className="flex items-center gap-2 px-3 py-1 bg-white border-b border-gray-200 flex-wrap">
          <span className="text-[11px] font-semibold text-gray-400">Color</span>
          {COLORS.map(c=><button key={c} onClick={()=>setStrokeColor(c)} className={`w-[18px] h-[18px] rounded-sm border ${strokeColor===c?'ring-2 ring-[#34A853] ring-offset-1 scale-125':'border-gray-200'} transition`} style={{background:c}} />)}
          <input type="color" value={strokeColor} onChange={e=>setStrokeColor(e.target.value)} className="w-[18px] h-[18px] rounded-sm cursor-pointer border-0 p-0" data-testid="draw-custom-color" />
          <div className="w-px h-5 bg-gray-200" />
          <span className="text-[11px] font-semibold text-gray-400">Fill</span>
          <button onClick={()=>setFillColor('')} className={`w-[18px] h-[18px] rounded-sm border text-[11px] flex items-center justify-center ${!fillColor?'ring-2 ring-[#34A853] ring-offset-1':'border-gray-200'}`} style={{background:'#fff'}}>∅</button>
          {COLORS.slice(0,10).map(c=><button key={`f${c}`} onClick={()=>setFillColor(c)} className={`w-[18px] h-[18px] rounded-sm border ${fillColor===c?'ring-2 ring-[#34A853] ring-offset-1 scale-125':'border-gray-200'} transition`} style={{background:c,opacity:0.7}} />)}
          <input type="color" value={fillColor||'#ffffff'} onChange={e=>setFillColor(e.target.value)} className="w-[18px] h-[18px] rounded-sm cursor-pointer border-0 p-0" />

          <div className="w-px h-5 bg-gray-200" />
          {/* Line width presets (jspaint-style box) */}
          <span className="text-[11px] font-semibold text-gray-400">Line</span>
          <div className="flex gap-0.5 bg-gray-50 rounded border border-gray-200 p-0.5">
            {LINE_WIDTHS.map(w => (
              <button key={w} onClick={()=>setStrokeWidth(w)} className={`w-6 h-5 flex items-center justify-center rounded ${strokeWidth===w?'bg-[#34A853]/20 ring-1 ring-[#34A853]':'hover:bg-white'}`} data-testid={`draw-linewidth-${w}`}>
                <div style={{width: '16px', height: `${Math.min(w, 8)}px`, background: '#1e293b', borderRadius: '1px'}} />
              </button>
            ))}
          </div>

          {/* Brush tips (only when brush tool is active) */}
          {tool === 'brush' && (
            <>
              <div className="w-px h-5 bg-gray-200" />
              <span className="text-[11px] font-semibold text-gray-400">Tip</span>
              <div className="flex gap-0.5 bg-gray-50 rounded border border-gray-200 p-0.5">
                {BRUSH_TIPS.map(b => (
                  <button key={b.id} onClick={()=>setBrushTip(b.id)} className={`w-6 h-5 text-[14px] rounded ${brushTip===b.id?'bg-[#34A853]/20 ring-1 ring-[#34A853]':'hover:bg-white'}`} data-testid={`draw-brushtip-${b.id}`}>{b.label}</button>
                ))}
              </div>
            </>
          )}

          {/* Fill mode (for shapes) */}
          {(tool === 'shape' || tool === 'polygon') && (
            <>
              <div className="w-px h-5 bg-gray-200" />
              <span className="text-[11px] font-semibold text-gray-400">Mode</span>
              <div className="flex gap-0.5 bg-gray-50 rounded border border-gray-200 p-0.5">
                {FILL_MODES.map(f => (
                  <button key={f.id} onClick={()=>setFillMode(f.id)} className={`text-[11px] px-1.5 h-5 rounded ${fillMode===f.id?'bg-[#34A853] text-white':'text-gray-500 hover:bg-white'}`} title={f.desc} data-testid={`draw-fillmode-${f.id}`}>{f.label}</button>
                ))}
              </div>
            </>
          )}

          <div className="w-px h-5 bg-gray-200" />
          <button onClick={()=>setShowGrid(g=>!g)} className={`text-[11px] font-semibold px-2 py-1 rounded ${showGrid?'bg-green-50 text-[#1A7F37]':'text-gray-400 hover:bg-gray-100'}`} data-testid="draw-grid-toggle">Grid</button>
        </div>

        {/* ── Canvas ── */}
        <div className="flex-1 overflow-auto bg-[#d4d4d4] flex items-center justify-center p-3 relative" style={{cursor: tool==='select'?'default' : tool==='text'?'text' : tool==='eyedropper'?'crosshair' : tool==='magnifier'?'zoom-in' : tool==='minifier'?'zoom-out' : 'crosshair'}}>
          <div className="relative shadow-xl rounded-md overflow-visible" style={{width: CW*zoom, minWidth: CW*zoom, height: CH*zoom}}>
            {showGrid&&<div className="absolute inset-0 pointer-events-none z-10 rounded-md" style={{backgroundImage:'linear-gradient(rgba(0,0,0,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,0.06) 1px,transparent 1px)',backgroundSize:`${24*zoom}px ${24*zoom}px`}} />}
            <Stage
              ref={stageRef}
              width={CW*zoom}
              height={CH*zoom}
              scaleX={zoom}
              scaleY={zoom}
              onMouseDown={onStageDown}
              onMouseMove={onStageMove}
              onTouchStart={onStageDown}
              onTouchMove={onStageMove}
              onDblClick={() => { if (tool==='polygon') commitPolygon(); }}
              onContextMenu={e => e.evt.preventDefault()}
              data-testid="drawing-canvas"
            >
              <Layer>
                {rasterImg ? <KImage image={rasterImg} x={0} y={0} width={CW} height={CH} name="raster" listening={true} /> : <Rect x={0} y={0} width={CW} height={CH} fill="#ffffff" name="bg" />}
                {objects.map(renderObj)}

                {/* Floating selection preview */}
                {floatSel && (
                  <Group
                    x={floatSel.x} y={floatSel.y}
                    onMouseDown={e => { const pos = getPos(); setFloatSel(f => ({...f, dragging: true, dragOffX: pos.x - f.x, dragOffY: pos.y - f.y})); e.cancelBubble = true; }}
                  >
                    <KImage image={floatSel.image} x={0} y={0} width={floatSel.w} height={floatSel.h} />
                    <Rect x={0} y={0} width={floatSel.w} height={floatSel.h} name={OVERLAY} stroke="#000" strokeWidth={1} dash={[4,4]} dashOffset={floatSel.dashOffset||0} listening={false} />
                    <Rect x={0} y={0} width={floatSel.w} height={floatSel.h} name={OVERLAY} stroke="#fff" strokeWidth={1} dash={[4,4]} dashOffset={(floatSel.dashOffset||0)+4} listening={false} />
                  </Group>
                )}

                {/* Rectangular selection marquee */}
                {selection && (
                  <>
                    <Rect x={selection.x} y={selection.y} width={selection.w} height={selection.h} name={OVERLAY} stroke="#000" strokeWidth={1} dash={[4,4]} dashOffset={selection.dashOffset||0} fill="transparent" listening={false} />
                    <Rect x={selection.x} y={selection.y} width={selection.w} height={selection.h} name={OVERLAY} stroke="#fff" strokeWidth={1} dash={[4,4]} dashOffset={(selection.dashOffset||0)+4} fill="transparent" listening={false} />
                  </>
                )}

                {curvePreview}
                {polyPreview}
                {polyPts.map((p, i) => <Circle key={`pp${i}`} name={OVERLAY} x={p.x} y={p.y} radius={4/zoom} fill="#34A853" listening={false} />)}
                {curveStage.map((p, i) => <Circle key={`cp${i}`} name={OVERLAY} x={p.x} y={p.y} radius={4/zoom} fill="#3b82f6" listening={false} />)}

                <Transformer ref={trRef} boundBoxFunc={(o,n)=>({...n,width:Math.max(5,n.width),height:Math.max(5,n.height)})} />
              </Layer>
            </Stage>
            {editingTextId&&editObj&&(()=>{const stageEl=stageRef.current?.container();if(!stageEl)return null;const sb=stageEl.getBoundingClientRect();const sx=sb.width/CW,sy=sb.height/CH;const tw=Math.max(80,(editObj.width||200)*sx),th=Math.max(30,(editObj.height||editObj.fontSize*2)*sy);return <textarea ref={textRef} value={editObj.text||''} data-testid="draw-text-input" onChange={e=>{const v=e.target.value;setObjects(prev=>prev.map(o=>o.id===editingTextId?{...o,text:v}:o));}} onKeyDown={e=>{if(e.key==='Escape')commitText();}} onBlur={commitText} placeholder="Type here..." className="absolute z-20 border-2 border-blue-400 bg-white/95 outline-none resize overflow-auto p-1.5 shadow-lg" style={{left:editObj.x*sx,top:editObj.y*sy,width:tw,height:th,minWidth:80,minHeight:30,fontSize:Math.max(10,editObj.fontSize*sy),fontFamily:editObj.fontFamily,fontWeight:editObj.fontStyle?.includes('bold')?'bold':'normal',fontStyle:editObj.fontStyle?.includes('italic')?'italic':'normal',textDecoration:editObj.textDecoration||'none',textAlign:editObj.align||'left',color:editObj.fill||'#1e293b',lineHeight:1.3}} autoFocus />;})()}
          </div>
        </div>

        {/* ── Status bar (jspaint-style) ── */}
        <div className="flex items-center gap-3 px-3 py-1 bg-white border-t border-gray-200 text-[11px] text-gray-400">
          <span data-testid="draw-status-dims">{CW}×{CH}px</span>
          <span data-testid="draw-status-zoom">{Math.round(zoom*100)}%</span>
          <span data-testid="draw-status-cursor">Cursor: {cursor.x}, {cursor.y}</span>
          {selection && <span>Selection: {Math.round(selection.w)}×{Math.round(selection.h)}</span>}
          {floatSel && <span className="text-gray-600">Floating · {Math.round(floatSel.w)}×{Math.round(floatSel.h)} @ {Math.round(floatSel.x)},{Math.round(floatSel.y)}</span>}
          {tool === 'curve' && <span className="text-gray-600">Curve: click {3 - curveStage.length} more point(s)</span>}
          {tool === 'polygon' && polyPts.length > 0 && <span className="text-gray-600">Polygon: {polyPts.length} pts · double-click or Enter to close</span>}
          <span>{objects.length} obj</span>
          <div className="flex-1" />
          <span>Tool: <b className="text-gray-600">{tool}</b>{tool==='shape'?` (${shapeType})`:''}</span>
          <span className="text-gray-300">Ctrl+A · Ctrl+C/X/V · Esc cancel</span>
        </div>
      </div>
    </div>
  );
}
